'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackMarketing/useTrackMarketing';
import { useRef } from 'react';
import { ComponentTypes } from '../../../interfaces';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import { overlayRectangleVariants } from './OverlayRectangle.client.variants';
import { OverlayRectangleItem } from './OverlayRectangleItem';
import type { OverlayRectangleType } from './types';

/**
 * This component displays flexible teasers in different layout variants on static pages.
 */
export const OverlayRectangle = ({ data }: { data: OverlayRectangleType }) => {
  const {
    field_scaling_factor: styleScalingFactor = 1,
    field_layout_variant: styleType = '1a',
    field_border_color: styleBorderColor = 'rgba(0,0,0,0.5)',
    field_background_color: styleBackgroundColor = 'rgba(255,255,255,0.7)',
    field_alignment: styleAlignment = 'center',
    field_vertical_alignment: styleVerticalAlignment = 'center',
    field_link: link,
    field_tracking: linkTracking,
    field_link_anchor: linkAnchor,
    field_link_highlight: linkHighlight,
    field_link_searchfilter: linkFilter,
    field_bonus_number: linkBonusNumber,
  } = data;
  const tmpLink = buildLinkWithArguments(link, {
    field_tracking: linkTracking,
    field_link_anchor: linkAnchor,
    field_link_highlight: linkHighlight,
    field_link_searchfilter: linkFilter,
    field_bonus_number: linkBonusNumber,
  });

  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);
  const teaserRefLink = useRef<HTMLAnchorElement>(null);
  const { trackClick } = useTrackMarketing(data, teaserRefLink, finalLink);

  const [layoutVariant, horizontalVariant] = styleType;
  const className = (initClassName: string) =>
    overlayRectangleVariants({
      variant: Number.parseInt(layoutVariant, 10) as 1 | 2 | 3 | 4,
      horizontal: horizontalVariant === 'a' ? 'left' : 'right',
      vertical: styleVerticalAlignment,
      className: initClassName,
      scaleDown: !!(styleScalingFactor && styleScalingFactor !== 1),
    });
  const style = {
    border: `2px solid ${styleBorderColor}`,
    background: styleBackgroundColor,
    justifyContent: styleAlignment,
    alignContent: styleVerticalAlignment,
  };

  if (link)
    return (
      <Link
        className={className('pointer-events-auto')}
        ref={teaserRefLink}
        style={style}
        href={finalLink}
        onClick={() => trackClick()}
      >
        <OverlayRectangleItem data={data} />
      </Link>
    );

  return (
    <span className={className('pointer-events-none')} style={style}>
      <OverlayRectangleItem data={data} />
    </span>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({ [ComponentTypes.OverlayRectangle]: OverlayRectangle });
